import { DIV, H2 } from 'sdi/components/elements';
import tr, { Translated, toTranslated } from 'sdi/locale';
import { stringToParagraphs, uniq } from 'sdi/util';

import { setLayout } from 'view/src/events/app';

import { fromNullable, isSome, none, some } from 'fp-ts/lib/Option';
import { buttonGuard, makeLabel } from 'sdi/components/button';
import { inputNumber } from 'sdi/components/input/input-number';
import {
    endHarvest,
    setCircleRadius,
    setHarvestStep,
    startDrawing,
} from 'view/src/events/harvest';
import {
    formatRadius,
    getCircleOptions,
    getHarvestStep,
} from 'view/src/queries/harvest';
import {
    CircleOptions,
    Drawing,
    HarvestShape,
    Harvesting,
} from 'view/src/shape/types';
import { makeLabelAndIcon } from '../button';
import { getMapExtent } from 'view/src/queries/map';
import { getPointResolution } from 'ol/proj';
import { Extent, getCenter, getWidth } from 'ol/extent';
import { getMapCrsName } from 'sdi/app';
import { eqNumber } from 'fp-ts/lib/Eq';

/**
  User interaction

  1. startDrawing(geometryType) 
    - user picks point/line/polygon;
    - sets the map interaction to create mode so OpenLayers captures
   the drawn shape.
  2. When drawing completes, addFeature(f) is called with the resulting GeoJSON feature.

  Firing requests

  endDrawing resets the map interaction, then iterates over all visible layers 
  of the current map and calls request(mapId, metadataId, id, groupId) for each.
  request posts a request on /api/harvest

  Display

  addHarvestToMap injects the filtered features back as a new map layer so they render
  on the map.
*/
const buttonCancel = makeLabelAndIcon('cancel', 2, 'times', () =>
    tr.view('clearSpatialFilter'),
);

const buttonQueryPage = makeLabelAndIcon('navigate', 1, 'file', () =>
    tr.view('goToQueryPage'),
);

const harvestPointButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiatePoint'),
);
const harvestLineButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiateLine'),
);
const harvestPolygonButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiatePolygon'),
);
const harvestCircleButton = makeLabel('info', 2, () =>
    tr.view('harvestInitiateCircle'),
);

const renderTitle = () => H2({}, tr.view('harvestTitle'));

const wrapInfo = (t: Translated) => DIV({}, stringToParagraphs(t));

const renderInfo = (shape: HarvestShape) => {
    switch (shape) {
        case 'point':
            return wrapInfo(tr.view('harvestInfoPoint'));
        case 'line':
            return wrapInfo(tr.view('harvestInfoLine'));
        case 'polygon':
            return wrapInfo(tr.view('harvestInfoPolygon'));
        case 'circle':
            return wrapInfo(tr.view('harvestInfoCircle'));
    }
};

const circleRadiusButton = (radius: number) =>
    makeLabelAndIcon('select', 2, 'arrows-h', () =>
        toTranslated(formatRadius(radius)),
    );

const renderRadiusSummary = (radius: number) =>
    DIV(
        'harvest__radius-summary',
        tr.view('harvestRadiusSummary', { radius: formatRadius(radius) }),
    );

const radiusInput = () =>
    inputNumber({
        key: 'harvest-circle-radius',
        get: () => getCircleOptions().chain(o => o.radius),
        set: v => setCircleRadius(fromNullable(v)),
        attrs: {
            min: 1,
            step: 1,
            placeholder: tr.view('harvestRadiusPlaceholder'),
        },
    });

const confirmButton = makeLabel('confirm', 1, () => tr.core('confirm'));

const getExtentWidth = (extent: Extent) => {
    const center = getCenter(extent);
    const metersPerUnit = getPointResolution(getMapCrsName(), 1, center, 'm');
    return getWidth(extent) * metersPerUnit; // ground meters
};

const SCALE_LADDER = [
    1, 2, 3, 5, 10, 20, 30, 50, 100, 200, 300, 500, 1000, 2000, 3000, 5000,
    10000, 20000, 30000, 50000, 100000, 200000, 300000, 500000, 1000000,
    2000000,
];

const RATIOS = [1 / 20, 1 / 10, 1 / 2];
const getRadiuses = (width: number) => RATIOS.map(ratio => ratio * width);

const logDistance = (a: number, b: number) =>
    Math.abs(Math.log(a) - Math.log(b));

const snapToLadder = (radiusM: number) =>
    SCALE_LADDER.reduce((best, scaleTick) =>
        logDistance(radiusM, scaleTick) < logDistance(radiusM, best)
            ? scaleTick
            : best,
    );

const getRadiusPresets = () =>
    getMapExtent()
        .map(getExtentWidth)
        .map(getRadiuses)
        .map(radiuses => radiuses.map(snapToLadder))
        .map(uniq(eqNumber))
        .getOrElse([50, 100, 500]);

const renderCircleOptions = (options: CircleOptions) =>
    DIV(
        'sidebar-main tool harvest',
        DIV('', tr.view('harvestInfoCircle')),
        DIV(
            'harvest__radius',
            getRadiusPresets().map(radius =>
                circleRadiusButton(radius)(() =>
                    startDrawing({ shape: 'circle', radius }),
                ),
            ),
        ),
        DIV(
            'harvest__input',
            radiusInput(),
            buttonGuard(isSome(options.radius), confirmButton, () =>
                startDrawing({
                    shape: 'circle',
                    radius: options.radius.getOrElse(0), // we know from the guard the option is some
                }),
            ),
        ),
        DIV(
            'harvest__actions filter-action__wrapper',
            buttonCancel(endHarvest),
        ),
    );

const initiateHarvest = (shape: HarvestShape) =>
    shape === 'circle'
        ? setHarvestStep({ state: 'circle-options', radius: none })
        : startDrawing({ shape });

const renderIdle = () =>
    DIV(
        'sidebar-main tool spatial-filter',
        H2({}, tr.view('harvestTitle')),
        DIV('', tr.view('spatialFilterHelpText')),
        DIV(
            'filter-selection__wrapper ',
            harvestPointButton(() => initiateHarvest('point')),
            harvestLineButton(() => initiateHarvest('line')),
            harvestPolygonButton(() => initiateHarvest('polygon')),
            harvestCircleButton(() => initiateHarvest('circle')),
        ),
    );

const renderDrawing = (harvest: Drawing) =>
    DIV(
        'sidebar-main tool spatial-filter activated',
        renderTitle(),
        renderInfo(harvest.shape),
        harvest.shape === 'circle'
            ? some(renderRadiusSummary(harvest.radius))
            : none,
        DIV('filter-action__wrapper', buttonCancel(endHarvest)),
    );

const renderHarvesting = (harvest: Harvesting) =>
    DIV(
        'sidebar-main tool spatial-filter activated',
        renderTitle(),
        renderInfo(harvest.shape),
        harvest.shape === 'circle'
            ? some(renderRadiusSummary(harvest.radius))
            : none,
        DIV(
            'filter-action__wrapper',
            buttonQueryPage(() => setLayout('query')),
            buttonCancel(endHarvest),
        ),
    );

export const renderHarvest = () => {
    const harvest = getHarvestStep();
    switch (harvest.state) {
        case 'idle':
            return renderIdle();
        case 'circle-options':
            return renderCircleOptions(harvest);
        case 'drawing':
            return renderDrawing(harvest);
        case 'harvesting':
            return renderHarvesting(harvest);
    }
};

export default renderHarvest;
